import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '../../../components/ui/input';
import { useAuthStore } from '../../../stores/authStore';
import { useUpdateProfile } from '../hooks/useSettings';
import { useFormGuard } from '../../../hooks/useFormGuard';
import { EmailChangeFlow } from './EmailChangeFlow';
import { UnsavedChangesModal, LoadingButton } from '../../../components/common';

const profileSchema = z.object({
  name: z.string().min(1, '名前を入力してください').max(100, '名前は100文字以内で入力してください'),
  email_notifications: z.boolean(),
  weekly_report_enabled: z.boolean(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export const ProfileSettings: React.FC = () => {
  const { user } = useAuthStore();
  const { mutate: updateProfile, isPending } = useUpdateProfile();
  const [emailChangeOpen, setEmailChangeOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user?.name || '',
      email_notifications: user?.email_notifications ?? true,
      weekly_report_enabled: user?.weekly_report_enabled ?? false,
    },
  });

  const { showModal, onDiscard, onContinue } = useFormGuard(isDirty);

  const onSubmit = (data: ProfileFormData) => {
    updateProfile(data, { onSuccess: () => reset(data) });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

        {/* プロフィールカード */}
        <div className="border rounded-lg">
          <div className="px-6 py-5 border-b">
            <h3 className="text-base font-semibold">プロフィール</h3>
          </div>
          <div className="p-6 space-y-5">

            {/* お名前 */}
            <div>
              <label className="block text-sm font-medium mb-1.5">お名前</label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <div>
                    <Input {...field} />
                    {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>}
                  </div>
                )}
              />
            </div>

            {/* メールアドレス */}
            <div>
              <label className="block text-sm font-medium mb-1.5">メールアドレス</label>
              <div className="flex items-center gap-2">
                <Input value={user?.email || ''} disabled className="flex-1" />
                {user?.auth_provider !== 'google' && (
                  <button
                    type="button"
                    data-testid="change-email-button"
                    onClick={() => setEmailChangeOpen(true)}
                    className="shrink-0 px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                  >
                    変更
                  </button>
                )}
              </div>
              {user?.pending_email && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800 mt-1.5"
                  data-testid="email-pending-badge"
                >
                  変更確認中: {user.pending_email}
                </span>
              )}
              <p className="text-xs text-gray-400 mt-1.5">変更すると新しいアドレスに確認コードをお送りします</p>
            </div>

          </div>
        </div>

        {/* 通知設定カード */}
        <div className="border rounded-lg">
          <div className="px-6 py-5 border-b">
            <h3 className="text-base font-semibold">通知設定</h3>
          </div>
          <div className="p-6">

            {/* 週次レポートトグル */}
            <Controller
              name="weekly_report_enabled"
              control={control}
              render={({ field }) => (
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">週次レポートをメールで受け取る</p>
                    <p className="text-xs text-gray-500 mt-0.5">毎週月曜日に会話数・リード獲得のサマリをお届けします</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={field.value}
                    data-testid="weekly-report-toggle"
                    onClick={() => field.onChange(!field.value)}
                    className="relative inline-flex shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none"
                    style={{ width: 44, height: 24, backgroundColor: field.value ? '#0a7a72' : '#D1D5DB' }}
                  >
                    <span
                      className="inline-block rounded-full bg-white shadow transition-transform duration-200"
                      style={{ width: 20, height: 20, marginTop: 2, transform: field.value ? 'translateX(22px)' : 'translateX(2px)' }}
                    />
                  </button>
                </div>
              )}
            />

          </div>
        </div>

        {/* 保存ボタン */}
        <div className="flex justify-end">
          <LoadingButton
            type="submit"
            loading={isPending}
            disabled={!isDirty}
          >
            変更を保存
          </LoadingButton>
        </div>

      </form>

      <EmailChangeFlow open={emailChangeOpen} onClose={() => setEmailChangeOpen(false)} />
      <UnsavedChangesModal isOpen={showModal} onDiscard={onDiscard} onContinue={onContinue} />
    </>
  );
};
